import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, pw);
      toast.success("Welcome back");
      nav(u.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr,1.1fr] bg-background">
      <div className="flex items-center justify-center px-6 py-16 lg:py-20 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <div className="logo-mark">EK</div>
            <span className="font-display font-semibold text-navy">EcomKit</span>
          </Link>
          <h1 className="font-display font-bold text-4xl tracking-tighter text-navy">Sign in</h1>
          <p className="text-sm text-muted-foreground mt-2 mb-8">Access your EcomKit command center.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="eyebrow block mb-1.5">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="input-base" placeholder="you@company.com"/>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Password</label>
              <input data-testid="login-password" type="password" required value={pw} onChange={(e)=>setPw(e.target.value)}
                className="input-base" placeholder="••••••••"/>
            </div>
            <button data-testid="login-submit" disabled={loading} type="submit" className="btn-primary w-full justify-center py-3">
              {loading ? "Signing in…" : <>Sign in <ArrowRight size={14} weight="bold"/></>}
            </button>
          </form>

          <div className="mt-8 text-sm text-muted-foreground">
            New to EcomKit? <Link to="/signup" className="text-orange font-semibold">Create an account</Link>
          </div>
          {process.env.NODE_ENV !== "production" && (
            <div className="mt-6 border border-dashed border-border rounded-md p-3 text-xs text-muted-foreground font-mono-data bg-secondary/40">
              Dev-only admin: admin@ecc.app / admin1234 — rotate before production (see HOSTINGER_DEPLOYMENT.md §8)
            </div>
          )}
        </div>
      </div>

      <div className="hidden lg:block relative bg-navy text-white order-1 lg:order-2 overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, hsl(var(--ecom-orange)) 0%, transparent 40%), radial-gradient(circle at 80% 80%, hsl(var(--ecom-green)) 0%, transparent 45%)"
        }}/>
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="eyebrow text-white/60">EcomKit · Command Center</div>
          <div>
            <h2 className="font-display font-bold text-5xl leading-[1.05] tracking-tighter">
              One catalog.<br/>Every marketplace.
            </h2>
            <p className="mt-4 text-white/70 text-lg max-w-md">
              Amazon, Flipkart, Meesho, Shopify — brought into a single source of truth.
            </p>
          </div>
          <div className="flex items-center gap-6 text-xs font-mono-data text-white/50">
            <span>SOC 2 ready</span>
            <span>GDPR compliant</span>
            <span>India-first</span>
          </div>
        </div>
      </div>
    </div>
  );
}
